import { useState } from "react"
import { submitFeedback, type FeedbackCategory } from "~/api/feedback"

const CATEGORIES: { id: FeedbackCategory; label: string }[] = [
  { id: "idea", label: "Suggest an idea" },
  { id: "problem", label: "Report a problem" },
  { id: "question", label: "Ask a question" },
  { id: "love", label: "Share something you love" },
]

export function ProfileFeedbackSection() {
  const [category, setCategory] = useState<FeedbackCategory>("idea")
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [thankYou, setThankYou] = useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSending(true)
    setError(null)
    try {
      const result = await submitFeedback(category, message)
      setThankYou(result.thank_you)
      setMessage("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send feedback")
    } finally {
      setSending(false)
    }
  }

  return (
    <section
      className="mt-6 border-t border-[var(--color-divider)] pt-4"
      aria-labelledby="profile-feedback-heading"
      data-testid="profile-feedback-section"
    >
      <h2
        id="profile-feedback-heading"
        className="text-lg font-semibold text-[var(--color-text-primary)]"
      >
        Help Shape Doggy Dynamo
      </h2>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        We&apos;re building Doggy Dynamo together with dog owners like you. Share an idea, report a
        problem, ask a question, or tell us what you love.
      </p>

      {thankYou ? (
        <p className="mt-4 rounded-lg border border-emerald-800/20 bg-emerald-50/90 p-3 text-sm text-emerald-950" role="status">
          {thankYou}
        </p>
      ) : (
        <form className="mt-4 flex flex-col gap-3" onSubmit={(e) => void handleSubmit(e)}>
          <fieldset>
            <legend className="text-sm font-medium text-[var(--color-text-primary)]">Category</legend>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CATEGORIES.map((item) => (
                <label
                  key={item.id}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-divider)] bg-white/70 px-3 py-2 text-sm"
                >
                  <input
                    type="radio"
                    name="feedback-category"
                    value={item.id}
                    checked={category === item.id}
                    onChange={() => setCategory(item.id)}
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="text-sm font-medium text-[var(--color-text-primary)]">
            Your message
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={4}
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white/80 px-3 py-2 text-sm text-[var(--color-text-primary)]"
              placeholder="Tell us what's on your mind…"
            />
          </label>

          {error && <p className="text-sm text-red-700">{error}</p>}

          <button
            type="submit"
            disabled={sending || message.trim().length === 0}
            className="rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send Feedback"}
          </button>
        </form>
      )}
    </section>
  )
}
